"use client";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  EllipsisVertical,
  Trash2,
} from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Field } from "@/shared/components/ui/field";
import { Label } from "@/shared/components/ui/label";
import { v4 } from "uuid";
import { ReactNode } from "react";
import { InputDebounced } from "../inputs/input-debounced";
import { useDecimalInputProps } from "../inputs/decimal-input";

/**
 * ETable is a table component with prepared cells for input.
 */
function ETable({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom border text-sm", className)}
        {...props}
      />
    </div>
  );
}

function ETableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  );
}

function ETableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", "[&_tr>*]:py-0", className)}
      {...props}
    />
  );
}

function ETableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function ETableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "data-[state=selected]:bg-muted border-b transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function ETableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-muted-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        "border", // suits bordered design
        className,
      )}
      {...props}
    />
  );
}

function ETableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        "border-r border-l", // too much space otherwise.
        className,
      )}
      {...props}
    />
  );
}

function ETableCellCheckbox({
  label,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  label?: string;
}) {
  const id = v4();
  return (
    <ETableCell>
      <Field orientation="horizontal">
        <Checkbox id={id} {...props} />
        {label && <Label htmlFor={id}>{label}</Label>}
      </Field>
    </ETableCell>
  );
}

/**
 * ETableCellInput provides a table cell working as a normal
 * input field.
 *
 * We use InputDebounce instead of Input here, since in the
 * table context we can expect large compontent trees regularly.
 * In the context of a generally debounced UI InputDebounce
 * helps pushing the need for more complex render performance
 * optimisations out.
 */
function ETableCellInput({
  className,
  colSpan,
  cellClassName,
  hasError,
  ...props
}: React.ComponentProps<"input"> & {
  colSpan?: number;
  cellClassName?: string;
  hasError?: boolean;
}) {
  return (
    <ETableCell className={cn("px-0", cellClassName)} colSpan={colSpan}>
      <InputDebounced
        className={cn(
          "dark:hover:bg-accent dark:bg-background dark:focus-visible:bg-accent border-none focus-visible:border-none focus-visible:ring-0",
          "field-sizing-content shadow-none [&[type='number']]:pr-0 [&[type='number']]:text-right",
          hasError ? "inset-ring inset-ring-red-500" : "",
          className,
        )}
        onBlur={(e) => {
          if (e.target.value !== e.target.defaultValue) {
            e.currentTarget.form?.requestSubmit();
          }
        }}
        {...props}
      />
    </ETableCell>
  );
}

/**
 * ETableCellDecimalInput is an {@link ETableCellInput} that always displays its
 * value padded to `precision` decimal places with a comma as the decimal
 * separator (German locale, e.g. `5` → `"5,00"`), including on initial load.
 *
 * It is {@link DecimalInput} in a table cell: same formatting, parsing and
 * re-sync behaviour, shared through {@link useDecimalInputProps}. Since
 * {@link ETableCellInput} buffers keystrokes, `onValueChange` is reported
 * debounced (drive your debounced save from there).
 *
 * @example
 * <ETableCellDecimalInput
 *   value={incidentalCost.materialFlatRate}
 *   onValueChange={setMaterialFlatRate}
 *   disabled={disabled}
 * />
 */
function ETableCellDecimalInput({
  value,
  onValueChange,
  precision,
  className,
  onFocus,
  onBlur,
  ...props
}: Omit<
  React.ComponentProps<typeof ETableCellInput>,
  "value" | "type" | "onChange"
> & {
  value: number;
  onValueChange: (value: number) => void;
  precision?: number;
}) {
  const decimalProps = useDecimalInputProps({
    value,
    onValueChange,
    precision,
    onFocus,
    onBlur,
  });

  return (
    <ETableCellInput
      {...props}
      {...decimalProps}
      className={cn("pr-4 text-right", className)}
    />
  );
}

function ETableCellSelect({
  className,
  label,
  placeholder,
  items,
  cellClassName,
  colSpan,
  rowSpan,
  hasError,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root> & {
  label: string;
  items: { value: string; label: ReactNode }[];
  cellClassName?: string;
  colSpan?: number;
  rowSpan?: number;
  className?: string;
  placeholder?: string;
  hasError?: boolean;
}) {
  return (
    <ETableCell
      className={cn("px-0", cellClassName)}
      colSpan={colSpan}
      rowSpan={rowSpan}
    >
      <Select {...props}>
        <SelectTrigger
          className={cn(
            "bg-background dark:bg-background dark:focus-visible:bg-accent w-full rounded-none border-none shadow-none focus-visible:border-none focus-visible:ring-0",
            hasError ? "inset-ring inset-ring-red-500" : "",
            className,
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent align="center">
          <SelectGroup>
            <SelectLabel>{label}</SelectLabel>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </ETableCell>
  );
}

function ETableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

/**
 * Props for ETableOptions component
 */
type ETableOptionsProps<T extends { id: string; order: number }> = {
  /** The data object of the current table row */
  item: T;
  /** The index of the current table row */
  rowIndex: number;
  /** The full list of objects of all table rows */
  items: T[];
  /** Callback for changes to the full list. */
  onItemsChange: (items: T[]) => void;
  /** A constructor to return a new object if a row is added.
   * If provided, the options "add row above / below" are shown. */
  newItem: () => T;
  /** A constructor to duplicate a given object.
   * If provided, the option "duplicate" is shown. */
  duplicate?: (item: T) => T;
  disabled?: boolean;
};

/**
 * ETableOptions offers a default set of operations to manipulate
 * the rows (and therefor items) in the table. This requires,
 * that the frontend has full control over item creation and deletion.
 */
function ETableOptions<T extends { id: string; order: number }>({
  item,
  rowIndex,
  items,
  onItemsChange,
  newItem,
  duplicate,
  disabled,
}: ETableOptionsProps<T>) {
  const doDelete = () => {
    onItemsChange(items.filter((i) => i.id !== item.id));
  };

  const insertRowAbove = () => {
    onItemsChange(addItem(items, newItem, rowIndex));
  };

  const insertRowBelow = () => {
    onItemsChange(addItem(items, newItem, rowIndex + 1));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="ghost" size="sm" className="rounded-none">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={insertRowAbove}>
            {t("Zeile darüber einfügen")}
            <DropdownMenuShortcut>
              <ChevronUp />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={insertRowBelow}>
            {t("Zeile darunter einfügen")}
            <DropdownMenuShortcut>
              <ChevronDown />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          {duplicate && (
            <DropdownMenuItem
              onClick={() =>
                onItemsChange(
                  addItem(items, () => duplicate(item), rowIndex + 1),
                )
              }
            >
              {t("Duplizieren")}
              <DropdownMenuShortcut>
                <Copy />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={doDelete}>
            {t("Zeile löschen")}
            <DropdownMenuShortcut>
              <Trash2 />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function addItem<T extends { id: string; order: number }>(
  items: T[],
  newItem: () => T,
  index?: number,
): T[] {
  if (index === undefined || index < 0 || index >= items.length) {
    return setOrderFieldToIndex([...items, newItem()]);
  }

  const rowsBefore = items.slice(0, index);
  const rowsAfter = items.slice(index);
  return setOrderFieldToIndex([...rowsBefore, newItem(), ...rowsAfter]);
}

function setOrderFieldToIndex<T extends { id: string; order: number }>(
  items: T[],
): T[] {
  return items.map((item, idx) => ({ ...item, order: idx }));
}

export {
  ETable,
  ETableHeader,
  ETableBody,
  ETableFooter,
  ETableHead,
  ETableRow,
  ETableCell,
  ETableCellCheckbox,
  ETableCellInput,
  ETableCellDecimalInput,
  ETableCellSelect,
  ETableCaption,
  ETableOptions,
  type ETableOptionsProps,
  setOrderFieldToIndex,
};
